import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Phone, Mail, MapPin } from "lucide-react";
import { FadeUp } from "./Reveal";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { BRAND } from "../../lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = { name: "", email: "", company: "", phone: "", service: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Enquiry received. Our team will reach out shortly.");
      setForm(EMPTY);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-white/20 py-4 font-body text-white placeholder:text-white/30 focus:border-[#00D166] focus:outline-none transition-colors";

  return (
    <section id="contact" className="relative py-24 lg:py-32" data-testid="contact-section">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5">
          <FadeUp>
            <span className="font-body text-xs tracking-[0.35em] text-[#00D166]">GET IN TOUCH</span>
            <h2 className="mt-4 font-display text-4xl md:text-6xl font-bold tracking-tight text-white">
              Let&apos;s build your <span className="font-serifed italic font-normal text-white/60">leadership.</span>
            </h2>
            <p className="mt-6 font-body text-white/60 max-w-md leading-relaxed">
              Share your mandate. We reply within one business day with a confidential, no-obligation conversation.
            </p>

            <div className="mt-12 space-y-6">
              <a href={`tel:${BRAND.phone}`} data-testid="contact-phone" className="flex items-center gap-4 group">
                <span className="grid h-11 w-11 place-items-center border border-white/15 text-[#00D166]"><Phone size={16} /></span>
                <span className="font-body text-white/80 group-hover:text-white transition-colors">{BRAND.phone}</span>
              </a>
              <a href={`mailto:${BRAND.email}`} data-testid="contact-email" className="flex items-center gap-4 group">
                <span className="grid h-11 w-11 place-items-center border border-white/15 text-[#00D166]"><Mail size={16} /></span>
                <span className="font-body text-white/80 group-hover:text-white transition-colors">{BRAND.email}</span>
              </a>
              <div className="flex items-center gap-4">
                <span className="grid h-11 w-11 place-items-center border border-white/15 text-[#00D166]"><MapPin size={16} /></span>
                <span className="font-body text-white/80">{BRAND.location}</span>
              </div>
            </div>
          </FadeUp>
        </div>

        <div className="lg:col-span-7">
          <FadeUp delay={0.1}>
            <form onSubmit={submit} className="grid sm:grid-cols-2 gap-8" data-testid="contact-form">
              <input data-testid="enquiry-name-input" className={inputCls} placeholder="Full name *" value={form.name} onChange={(e) => update("name", e.target.value)} />
              <input data-testid="enquiry-email-input" type="email" className={inputCls} placeholder="Email *" value={form.email} onChange={(e) => update("email", e.target.value)} />
              <input data-testid="enquiry-company-input" className={inputCls} placeholder="Company" value={form.company} onChange={(e) => update("company", e.target.value)} />
              <input data-testid="enquiry-phone-input" className={inputCls} placeholder="Phone" value={form.phone} onChange={(e) => update("phone", e.target.value)} />

              <div className="sm:col-span-2">
                <Select value={form.service} onValueChange={(v) => update("service", v)}>
                  <SelectTrigger data-testid="enquiry-service-select" className="w-full bg-transparent border-0 border-b border-white/20 rounded-none py-6 px-0 font-body text-white focus:ring-0 focus:border-[#00D166]">
                    <SelectValue placeholder="Service of interest" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0F172A] border-white/10 text-white">
                    <SelectItem value="Executive Search">Executive Search</SelectItem>
                    <SelectItem value="HR Consulting">HR Consulting</SelectItem>
                    <SelectItem value="Both / Not sure">Both / Not sure</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="sm:col-span-2">
                <textarea data-testid="enquiry-message-input" rows={4} className={inputCls + " resize-none"} placeholder="Tell us about your mandate *" value={form.message} onChange={(e) => update("message", e.target.value)} />
              </div>

              <div className="sm:col-span-2">
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="enquiry-submit-button"
                  className="group inline-flex items-center gap-3 font-body text-sm font-semibold bg-[#00D166] text-[#030712] px-8 py-4 hover:bg-white transition-colors duration-300 disabled:opacity-60"
                >
                  {loading ? "Sending..." : "Submit Enquiry"}
                  <ArrowUpRight size={18} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                </button>
              </div>
            </form>
          </FadeUp>
        </div>
      </div>
    </section>
  );
}
